<template>
  <b-modal
    v-model="visibleProxy"
    :title="modalTitle"
    size="xl"
    scrollable
    footer-class="hos-timeline-modal-footer"
    @hidden="$emit('hidden')"
  >
    <div v-if="loading" class="text-center py-4">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <div v-else>
      <div v-if="error" class="hos-timeline-error-banner">{{ error }}</div>

      <div v-if="summary.window_start">
      <div v-if="showTimeCompare" class="hos-time-compare">
        <div class="hos-time-compare-grid" :class="{ 'hos-time-compare-grid-with-dvir': showDvirColumn }">
          <div class="hos-time-compare-spacer"></div>
          <div class="hos-time-compare-head hos-time-planned">Planned</div>
          <div class="hos-time-compare-head hos-time-matched">HOS</div>
          <div class="hos-time-compare-head hos-time-vehicle">Vehicle</div>
          <div class="hos-time-compare-head hos-time-ignition">Ignition</div>
          <div v-if="showDvirColumn" class="hos-time-compare-head hos-time-dvir">DVIR</div>
          <div class="hos-time-compare-head">Delta</div>

          <div class="hos-time-compare-label">Start</div>
          <div class="hos-time-compare-value hos-time-planned">{{ plannedStartText }}</div>
          <div class="hos-time-compare-value hos-time-matched">{{ actualStartText }}</div>
          <div class="hos-time-compare-value hos-time-vehicle">{{ vehicleStartText }}</div>
          <div class="hos-time-compare-value hos-time-ignition">{{ ignitionStartText }}</div>
          <div v-if="showDvirColumn" class="hos-time-compare-value hos-time-dvir">{{ dvirStartText }}</div>
          <div class="hos-time-compare-value hos-time-compare-delta" :class="deltaClass(startDeltaMinutes)">
            {{ formatSignedDelta(startDeltaMinutes) }}
          </div>

          <div class="hos-time-compare-label">Finish</div>
          <div class="hos-time-compare-value hos-time-planned">{{ plannedFinishText }}</div>
          <div class="hos-time-compare-value hos-time-matched">{{ actualFinishText }}</div>
          <div class="hos-time-compare-value hos-time-vehicle">{{ vehicleFinishText }}</div>
          <div class="hos-time-compare-value hos-time-ignition">{{ ignitionFinishText }}</div>
          <div v-if="showDvirColumn" class="hos-time-compare-value hos-time-dvir">{{ dvirFinishText }}</div>
          <div class="hos-time-compare-value hos-time-compare-delta" :class="deltaClass(finishDeltaMinutes)">
            {{ formatSignedDelta(finishDeltaMinutes) }}
          </div>

          <div class="hos-time-compare-label">Duration</div>
          <div class="hos-time-compare-value hos-time-planned">{{ plannedDurationText }}</div>
          <div class="hos-time-compare-value hos-time-matched">{{ actualDurationText }}</div>
          <div class="hos-time-compare-value hos-time-vehicle">{{ vehicleDurationText }}</div>
          <div class="hos-time-compare-value hos-time-ignition">{{ ignitionDurationText }}</div>
          <div v-if="showDvirColumn" class="hos-time-compare-value hos-time-dvir">{{ dvirDurationText }}</div>
          <div class="hos-time-compare-value hos-time-compare-delta" :class="deltaClass(durationDeltaMinutes)">
            {{ formatSignedDelta(durationDeltaMinutes) }}
          </div>
        </div>
      </div>

      <div v-if="hasMultipleVehicles" class="hos-timeline-legend hos-timeline-vehicle-legend">
        <span class="hos-timeline-legend-heading">Vehicles</span>
        <span v-for="vehicle in vehicleLegend" :key="vehicle.id" class="hos-timeline-legend-item">
          <span
            class="hos-timeline-vehicle-swatch"
            :style="{ borderLeftColor: vehicleColor(vehicle.id) }"
          ></span>
          {{ vehicle.name || vehicle.id }}<span v-if="vehicle.is_booking_vehicle" class="hos-timeline-legend-booking"> · booking</span>
        </span>
      </div>

      <div v-if="referenceMarkerLegendItems.length" class="hos-timeline-legend hos-timeline-reference-legend">
        <span class="hos-timeline-legend-heading">Milestones</span>
        <button
          v-for="item in referenceMarkerLegendItems"
          :key="item.key"
          type="button"
          class="hos-timeline-legend-toggle"
          :class="{ 'hos-timeline-legend-toggle--off': !referenceMarkerVisibility[item.key] }"
          :aria-pressed="String(referenceMarkerVisibility[item.key])"
          :title="referenceMarkerVisibility[item.key] ? 'Hide ' + item.label + ' lines' : 'Show ' + item.label + ' lines'"
          @click="toggleReferenceMarker(item.key)"
        >
          <span
            class="hos-timeline-reference-swatch"
            :style="{ borderLeftColor: item.color }"
          ></span>
          {{ item.label }}
        </button>
        <span v-if="manualActualsMode" class="hos-timeline-reference-edit-hint">Drag milestone lines to adjust times</span>
      </div>

      <div
        v-for="(view, segIdx) in timelineViews"
        :key="'timeline-seg-' + segIdx"
        class="hos-timeline-chart-wrapper"
        :class="{ 'hos-timeline-chart-wrapper-split': summary.multiday_timeline }"
      >
        <div class="hos-timeline-axis-row hos-timeline-date-row">
          <div class="hos-timeline-axis-spacer"></div>
          <div class="hos-timeline-axis-track">
            <span
              v-for="(tick, idx) in view.dateChangeTicks"
              :key="'date-' + segIdx + '-' + idx"
              class="hos-timeline-date-tick"
              :class="axisTickAlignClass(tick.leftPct)"
              :style="{ left: tick.leftPct + '%' }"
            >{{ tick.label }}</span>
          </div>
        </div>

        <div class="hos-timeline-axis-row">
          <div class="hos-timeline-axis-spacer"></div>
          <div class="hos-timeline-axis-track">
            <template v-for="(tick, idx) in view.hourTicks">
              <span
                v-if="tick.label"
                :key="'axis-' + segIdx + '-' + idx"
                class="hos-timeline-axis-tick"
                :class="axisTickAlignClass(tick.leftPct)"
                :style="{ left: tick.leftPct + '%' }"
              >{{ tick.label }}</span>
            </template>
          </div>
        </div>

        <div class="hos-timeline-chart">
          <div
            v-if="view.midnightGridLines.length"
            class="hos-timeline-chart-midnight-grid"
          >
            <div
              v-for="(tick, idx) in view.midnightGridLines"
              :key="'midnight-' + segIdx + '-' + idx"
              class="hos-timeline-midnight-line"
              :style="{ left: tick.leftPct + '%' }"
            ></div>
          </div>
          <div
            v-if="visibleTimelineMarkers(view).length || visibleDvirMarkers(view).length"
            class="hos-timeline-chart-reference-markers"
            :class="{ 'hos-timeline-chart-reference-markers--edit-mode': manualActualsMode }"
          >
            <div
              v-for="(marker, idx) in visibleTimelineMarkers(view)"
              :key="'marker-' + segIdx + '-' + idx"
              class="hos-timeline-marker"
              :class="[
                marker.className,
                {
                  'hos-timeline-marker--draggable': manualActualsMode && marker.field,
                  'hos-timeline-marker--dragging': markerDrag && markerDrag.field === marker.field,
                },
              ]"
              :style="markerStyle(marker, view)"
              :title="marker.title"
              @mousedown.prevent="onMarkerMouseDown($event, marker, view, segIdx)"
            ></div>
            <div
              v-for="(marker, idx) in visibleDvirMarkers(view)"
              :key="'dvir-marker-' + segIdx + '-' + idx"
              class="hos-timeline-marker hos-marker-dvir"
              :class="{
                'hos-timeline-marker--draggable': manualActualsMode && marker.field,
                'hos-timeline-marker--dragging': markerDrag && markerDrag.field === marker.field,
              }"
              :style="markerStyle(marker, view)"
              :title="marker.title"
              @mousedown.prevent="onMarkerMouseDown($event, marker, view, segIdx)"
            ></div>
          </div>
          <div class="hos-timeline-row hos-timeline-state-row">
            <div class="hos-timeline-row-label hos-timeline-state-labels">
              <span>Off Duty</span>
              <span>Driving</span>
              <span>On Duty</span>
            </div>
            <div
              class="hos-timeline-track hos-timeline-state-track"
              @mousedown.prevent="onTimelineZoomMouseDown($event, segIdx, view)"
            >
              <div
                v-for="(tick, idx) in view.hourTicks"
                :key="'grid-state-' + segIdx + '-' + idx"
                class="hos-timeline-grid-line"
                :style="{ left: tick.leftPct + '%' }"
              ></div>
              <div class="hos-state-band hos-state-band-offDuty"></div>
              <div class="hos-state-band hos-state-band-driving"></div>
              <div class="hos-state-band hos-state-band-onDuty"></div>
              <svg
                class="hos-state-line"
                viewBox="0 0 100 72"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  v-if="view.hosStepPath"
                  :d="view.hosStepPath"
                  class="hos-state-line-path"
                />
              </svg>
              <div
                v-for="(log, idx) in view.hosLogHits"
                :key="'state-hit-' + segIdx + '-' + idx"
                class="hos-state-hit"
                :style="stateHitStyle(log, view)"
                :title="logHoverTitle(log)"
              ></div>
            </div>
          </div>
          <div v-if="view.showVehicleRow" class="hos-timeline-row hos-timeline-vehicle-row">
            <div class="hos-timeline-row-label">Vehicle</div>
            <div
              class="hos-timeline-track hos-timeline-vehicle-track"
              @mousedown.prevent="onTimelineZoomMouseDown($event, segIdx, view)"
            >
              <div
                v-for="(tick, idx) in view.hourTicks"
                :key="'grid-vehicle-' + segIdx + '-' + idx"
                class="hos-timeline-grid-line"
                :style="{ left: tick.leftPct + '%' }"
              ></div>
              <div
                v-for="(segment, idx) in view.vehicleSegments"
                :key="'vehicle-' + segIdx + '-' + idx"
                class="hos-timeline-vehicle-segment"
                :class="{ 'hos-timeline-vehicle-segment-booking': segment.is_booking_vehicle }"
                :style="vehicleSegmentStyle(segment, view)"
                :title="vehicleSegmentTitle(segment)"
              >
                <span class="hos-timeline-vehicle-label">{{ segment.label }}</span>
              </div>
            </div>
          </div>
          <div v-if="view.showIgnitionRow" class="hos-timeline-row hos-timeline-ignition-row">
            <div class="hos-timeline-row-label hos-timeline-ignition-labels">
              <span>Off</span>
              <span>On</span>
              <span>Idle</span>
            </div>
            <div
              class="hos-timeline-track hos-timeline-ignition-track"
              @mousedown.prevent="onTimelineZoomMouseDown($event, segIdx, view)"
            >
              <div
                v-for="(tick, idx) in view.hourTicks"
                :key="'grid-ignition-' + segIdx + '-' + idx"
                class="hos-timeline-grid-line"
                :style="{ left: tick.leftPct + '%' }"
              ></div>
              <div class="hos-ignition-band hos-ignition-band-off"></div>
              <div class="hos-ignition-band hos-ignition-band-on"></div>
              <div class="hos-ignition-band hos-ignition-band-idle"></div>
              <svg
                class="hos-ignition-line"
                viewBox="0 0 100 72"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  v-if="view.ignitionStepPath"
                  :d="view.ignitionStepPath"
                  class="hos-ignition-line-path"
                />
              </svg>
              <div
                v-for="(segment, idx) in view.ignitionLogHits"
                :key="'ignition-hit-' + segIdx + '-' + idx"
                class="hos-ignition-hit"
                :style="ignitionHitStyle(segment, view)"
                :title="ignitionHoverTitle(segment)"
              ></div>
            </div>
          </div>
          <div
            v-if="zoomSelect && zoomSelect.segIdx === segIdx"
            class="hos-timeline-zoom-layer"
          >
            <div
              class="hos-timeline-zoom-selection"
              :style="zoomSelectionStyle(segIdx)"
            ></div>
          </div>
        </div>
        <div class="hos-timeline-axis-row hos-timeline-axis-row-bottom">
          <div class="hos-timeline-axis-spacer"></div>
          <div class="hos-timeline-axis-track">
            <template v-for="(tick, idx) in view.hourTicks">
              <span
                v-if="tick.label"
                :key="'axis-bottom-' + segIdx + '-' + idx"
                class="hos-timeline-axis-tick"
                :class="axisTickAlignClass(tick.leftPct)"
                :style="{ left: tick.leftPct + '%' }"
              >{{ tick.label }}</span>
            </template>
          </div>
        </div>
      </div>

      <div v-if="events.length === 0" class="hos-timeline-empty">
        <span v-if="error">HOS timeline not available.</span>
        <span v-else>No HOS events found in telematics for this window.</span>
      </div>
    </div>

    <div v-else-if="!error" class="text-muted py-3">
      Unable to load actuals details for this booking.
    </div>
    </div>

    <template #modal-footer>
      <div class="hos-timeline-modal-footer-wrap">
        <div class="hos-timeline-modal-footer-inner">
          <div class="hos-timeline-modal-footer-left">
            <div v-if="showNav" class="hos-timeline-modal-nav">
              <button
                type="button"
                class="hos-timeline-icon-btn"
                :disabled="!navPrevEnabled || loading || reprocessing"
                title="Previous actual"
                aria-label="Previous actual"
                @click="$emit('prev')"
              >
                <i class="fa fa-chevron-left" aria-hidden="true"></i>
              </button>
              <span class="hos-timeline-nav-label">{{ navLabel }}</span>
              <button
                type="button"
                class="hos-timeline-icon-btn"
                :disabled="!navNextEnabled || loading || reprocessing"
                title="Next actual"
                aria-label="Next actual"
                @click="$emit('next')"
              >
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <div v-if="showTimelineZoomControls" class="hos-timeline-modal-footer-center">
            <div v-if="timelineViews.length > 1" class="hos-timeline-footer-segment-switch">
              <button
                v-for="timelineView in timelineViews"
                :key="'footer-seg-' + timelineView.segmentIndex"
                type="button"
                class="hos-timeline-footer-segment-btn"
                :class="{ 'hos-timeline-footer-segment-btn-active': zoomSegmentIndex === timelineView.segmentIndex }"
                @click="selectedFooterTimelineSegment = timelineView.segmentIndex"
              >{{ footerSegmentLabel(timelineView.segmentLabel) }}</button>
            </div>
            <div class="hos-timeline-zoom-controls">
              <button
                type="button"
                class="hos-timeline-icon-btn"
                :class="{ 'hos-timeline-icon-btn-active': isRelevantActivityZoomActive(zoomSegmentIndex) }"
                :disabled="!canZoomToRelevantActivityForSegment(zoomSegmentIndex)"
                :title="isRelevantActivityZoomActive(zoomSegmentIndex)
                  ? 'Reset zoom to full range'
                  : 'Zoom to planned, HOS, vehicle, ignition, and DVIR milestone times'"
                :aria-label="isRelevantActivityZoomActive(zoomSegmentIndex)
                  ? 'Reset zoom to full range'
                  : 'Zoom to milestone times'"
                @click="zoomToRelevantActivity(zoomSegmentIndex)"
              >
                <i class="fa fa-crosshairs" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="hos-timeline-icon-btn"
                :disabled="!canPanTimelineEarlier(zoomSegmentIndex)"
                title="Shift view earlier"
                aria-label="Shift view earlier"
                @click="panTimelineEarlier(zoomSegmentIndex)"
              >
                <i class="fa fa-chevron-left" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="hos-timeline-icon-btn"
                :disabled="!canZoomTimelineOut(zoomSegmentIndex)"
                title="Zoom out"
                aria-label="Zoom out"
                @click="zoomTimelineOut(zoomSegmentIndex)"
              >
                <i class="fa fa-search-minus" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="hos-timeline-icon-btn"
                :disabled="!canZoomTimelineIn(zoomSegmentIndex)"
                title="Zoom in"
                aria-label="Zoom in"
                @click="zoomTimelineIn(zoomSegmentIndex)"
              >
                <i class="fa fa-search-plus" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="hos-timeline-icon-btn"
                :disabled="!canPanTimelineLater(zoomSegmentIndex)"
                title="Shift view later"
                aria-label="Shift view later"
                @click="panTimelineLater(zoomSegmentIndex)"
              >
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="hos-timeline-icon-btn"
                :disabled="!isSegmentZoomed(zoomSegmentIndex)"
                title="Reset zoom to full range"
                aria-label="Reset zoom to full range"
                @click="resetSegmentZoom(zoomSegmentIndex)"
              >
                <i class="fa fa-compress" aria-hidden="true"></i>
              </button>
            </div>
          </div>
          <div class="hos-timeline-modal-footer-right">
            <div class="hos-timeline-modal-footer-actions">
            <actuals-feedback-controls
              v-if="feedbackItem && tenant"
              :item="feedbackItem"
              :tenant="tenant"
              :is-tbn="isTbn"
              :audit-mode="feedbackEnabled"
              class="hos-timeline-modal-feedback"
              @feedback-saved="onModalFeedbackSaved"
              @showAlert="(msg, variant, timeout) => $emit('showAlert', msg, variant, timeout)"
            />
            <b-button variant="primary" @click="visibleProxy = false">Close</b-button>
            </div>
          </div>
        </div>
        <div
          v-if="summary.window_start || showAdminActions || processedTimeText"
          class="hos-timeline-modal-footer-secondary"
        >
          <div v-if="summary.window_start" class="hos-timeline-modal-footer-manual">
            <button
              type="button"
              class="hos-timeline-icon-btn"
              :class="{ 'hos-timeline-icon-btn-active': manualActualsMode }"
              :disabled="loading || savingManualActuals"
              :title="manualActualsMode ? 'Exit manual actuals mode' : 'Edit actual times manually'"
              @click="toggleManualActualsMode"
            >
              <i class="fa fa-pencil" aria-hidden="true"></i>
            </button>
            <button
              v-if="manualActualsMode"
              type="button"
              class="hos-timeline-icon-btn"
              :disabled="!hasManualActualsEdits || savingManualActuals"
              title="Save manual actual times"
              @click="saveManualActualTimes"
            >
              <i :class="savingManualActuals ? 'fa fa-spinner fa-spin' : 'fa fa-save'" aria-hidden="true"></i>
            </button>
            <button
              v-if="manualActualsMode"
              type="button"
              class="hos-timeline-icon-btn"
              :disabled="!hasManualActualsEdits || savingManualActuals"
              title="Reset to calculated times"
              @click="resetManualActualsEdits"
            >
              <i class="fa fa-undo" aria-hidden="true"></i>
            </button>
          </div>
          <div v-if="showAdminActions" class="hos-timeline-modal-footer-admin">
            <button
              v-if="bookingIdToCopy"
              type="button"
              class="hos-timeline-icon-btn"
              :title="bookingIdCopied ? 'Copied!' : ('Copy booking ID: ' + bookingIdToCopy)"
              @click="copyBookingId"
            >
              <i :class="bookingIdCopied ? 'fa fa-check' : 'fa fa-copy'" aria-hidden="true"></i>
            </button>
            <button
              v-if="isTbn"
              type="button"
              class="hos-timeline-icon-btn"
              :disabled="reprocessing || loading"
              :title="reprocessing ? 'Reprocessing…' : 'Reprocess this actual'"
              @click="$emit('reprocess')"
            >
              <i :class="reprocessing ? 'fa fa-spinner fa-spin' : 'fa fa-history'" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              class="hos-timeline-icon-btn"
              :disabled="reprocessing || loading"
              title="Reload details"
              @click="$emit('reload')"
            >
              <i class="fa fa-refresh" aria-hidden="true"></i>
            </button>
          </div>
          <div v-if="processedTimeText" class="hos-timeline-processed-time-footer">
            Last processed: {{ processedTimeText }}
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment-timezone'
import actuals_feedback_controls from '@/components/actuals_feedback_controls'
import { copyToClipboard } from '@/helpers/vsm_standalone_url'
import {
  canZoomToRelevantActivity,
  computeRelevantActivityWindow,
  isZoomedToRelevantActivity,
} from '@/helpers/actuals_hos_timeline_activity_window'
import { save_manual_actual_times } from '@/networking/apis'
import { save_tbn_manual_actual_times } from '@/networking/tbn_apis'

const STATUS_LABELS = {
  offDuty: 'Off Duty',
  onDuty: 'On Duty',
  driving: 'Driving',
}

const STATUS_LEVELS = {
  offDuty: 0,
  driving: 1,
  onDuty: 2,
}

const STATE_LINE_VIEW_HEIGHT = 72

const IGNITION_STATUS_LABELS = {
  off: 'Off',
  on: 'On',
  idle: 'Idle',
}

const IGNITION_STATUS_LEVELS = {
  off: 0,
  on: 1,
  idle: 2,
}

const VEHICLE_COLORS = ['#0d6efd', '#6f42c1', '#fd7e14', '#20c997', '#d63384', '#6610f2']

const REFERENCE_MARKER_LEGEND = [
  { key: 'planned', label: 'Planned', className: 'hos-marker-planned', color: '#0d6efd', fields: ['planned_start', 'planned_finish'] },
  { key: 'matched', label: 'HOS', className: 'hos-marker-matched', color: '#198754', fields: ['hos_start', 'hos_finish'] },
  { key: 'vehicle', label: 'Vehicle', className: 'hos-marker-vehicle', color: '#6f42c1', fields: ['vehicle_start', 'vehicle_finish'] },
  { key: 'ignition', label: 'Ignition', className: 'hos-marker-ignition', color: '#d63384', fields: ['ignition_start', 'ignition_finish'] },
  { key: 'dvir', label: 'DVIR', className: 'hos-marker-dvir', color: '#fd7e14', fields: ['dvir_start', 'dvir_finish'] },
]

const DEFAULT_REFERENCE_MARKER_VISIBILITY = {
  planned: true,
  matched: true,
  vehicle: true,
  ignition: true,
  dvir: true,
}

const EDITABLE_SUMMARY_TIME_FIELDS = [
  'hos_start',
  'hos_finish',
  'vehicle_start',
  'vehicle_finish',
  'ignition_start',
  'ignition_finish',
  'dvir_start',
  'dvir_finish',
]

const MIN_TIMELINE_ZOOM_SPAN_MS = 15 * 60 * 1000
const TIMELINE_ZOOM_FULL_RANGE_TOLERANCE_MS = 500

export default {
  name: 'actuals_hos_timeline_modal',

  components: {
    'actuals-feedback-controls': actuals_feedback_controls,
  },

  props: {
    visible: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' },
    summary: { type: Object, default: () => ({}) },
    events: { type: Array, default: () => [] },
    ignitionEvents: { type: Array, default: () => [] },
    feedbackItem: { type: Object, default: null },
    tenant: { type: String, default: '' },
    isTbn: { type: Boolean, default: false },
    feedbackEnabled: { type: Boolean, default: false },
    showAdminActions: { type: Boolean, default: false },
    reprocessing: { type: Boolean, default: false },
    navPrevEnabled: { type: Boolean, default: false },
    navNextEnabled: { type: Boolean, default: false },
    navLabel: { type: String, default: '' },
  },

  data() {
    return {
      bookingIdCopied: false,
      bookingIdCopiedTimer: null,
      timelineZoomBySegment: {},
      zoomSelect: null,
      selectedFooterTimelineSegment: 0,
      referenceMarkerVisibility: { ...DEFAULT_REFERENCE_MARKER_VISIBILITY },
      manualActualsMode: false,
      calculatedTimes: {},
      editedTimes: {},
      markerDrag: null,
      savingManualActuals: false,
    }
  },

  watch: {
    visible(value) {
      if (!value) {
        this.resetAllTimelineZoom()
        this.resetReferenceMarkerVisibility()
        this.exitManualActualsMode()
      }
    },
    'summary.window_start'() {
      this.resetAllTimelineZoom()
      this.selectedFooterTimelineSegment = 0
      this.snapshotCalculatedTimes()
      this.editedTimes = {}
      this.manualActualsMode = false
    },
  },

  beforeDestroy() {
    if (this.bookingIdCopiedTimer)
      clearTimeout(this.bookingIdCopiedTimer)
    this.removeZoomSelectListeners()
    this.removeMarkerDragListeners()
  },

  computed: {
    showNav() {
      return !!this.navLabel || this.navPrevEnabled || this.navNextEnabled
    },
    bookingIdToCopy() {
      const item = this.feedbackItem || {}
      return item.ID || item.bookingID || (this.summary && this.summary.movement_id) || ''
    },
    visibleProxy: {
      get() {
        return this.visible
      },
      set(value) {
        this.$emit('update:visible', value)
      },
    },
    modalTitle() {
      const parts = ['Actuals Details']
      if (this.tripDayText)
        parts.push(this.tripDayText)
      if (this.summary && this.summary.driver_name)
        parts.push('Driver: ' + this.summary.driver_name)
      if (this.summary) {
        const vehicleLabel = this.summary.multiple_vehicles ? 'Booking vehicle' : 'Vehicle'
        parts.push(vehicleLabel + ': ' + (this.summary.vehicle || '(not set)'))
      }
      if (this.summary && this.summary.charter_id)
        parts.push('Charter: ' + this.summary.charter_id)
      if (this.summary && this.summary.movement_id)
        parts.push('Movement: ' + this.summary.movement_id)
      return parts.join(' — ')
    },
    tripDayText() {
      const day = this.resolveTripDayValue('day_of_trip')
      if (day == null)
        return ''
      const finalDay = this.resolveTripDayValue('final_day_of_trip')
      if (finalDay != null)
        return 'Day ' + day + ' of ' + finalDay
      return 'Day ' + day
    },
    processedTimeText() {
      const value = (this.summary && this.summary.processed_time)
        || (this.feedbackItem && this.feedbackItem.processed_time)
      if (!value)
        return ''
      const m = this.tenantMoment(value)
      if (!m.isValid())
        return value
      const fmt = (this.summary && this.summary.datetime_format) || 'MM/DD hh:mm A'
      return m.format(fmt)
    },
    effectiveSummary() {
      const base = this.summary || {}
      if (!Object.keys(this.editedTimes).length)
        return base
      return { ...base, ...this.editedTimes }
    },
    hasManualActualsEdits() {
      return EDITABLE_SUMMARY_TIME_FIELDS.some(field => this.manualTimeChanged(field))
    },
    windowStartMs() {
      return this.parseTimeMs(this.summary.window_start) || 0
    },
    windowEndMs() {
      return this.parseTimeMs(this.summary.window_end) || 0
    },
    timelineViews() {
      const summarySnapshot = this.effectiveSummary
      void summarySnapshot
      const segments = this.getTimelineSegments()
      return segments.map((segment, segIdx) => {
        const baseStartMs = this.parseTimeMs(segment.window_start)
        const baseEndMs = this.parseTimeMs(segment.window_end)
        const zoom = this.timelineZoomBySegment[segIdx]
        let effectiveSegment = segment
        if (zoom && baseStartMs != null && baseEndMs != null) {
          effectiveSegment = {
            ...segment,
            window_start: Math.max(baseStartMs, zoom.startMs),
            window_end: Math.min(baseEndMs, zoom.endMs),
          }
        }
        const view = this.buildTimelineView(effectiveSegment)
        if (!view)
          return null
        view.segmentIndex = segIdx
        view.baseWindowStartMs = baseStartMs
        view.baseWindowEndMs = baseEndMs
        return view
      }).filter(view => view)
    },
    showTimelineZoomControls() {
      return !!(this.summary.window_start && this.timelineViews.length)
    },
    zoomSegmentIndex() {
      if (!this.timelineViews.length)
        return 0
      if (this.timelineViews.length === 1)
        return this.timelineViews[0].segmentIndex
      return this.selectedFooterTimelineSegment
    },
    vehicleLegend() {
      return (this.summary && this.summary.vehicles) || []
    },
    hasMultipleVehicles() {
      if (this.summary && this.summary.multiple_vehicles)
        return true
      return this.vehicleLegend.length > 1
    },
    referenceMarkerLegendItems() {
      if (!this.summary)
        return []
      return REFERENCE_MARKER_LEGEND.filter(item => {
        if (item.key === 'dvir' && !this.showDvirColumn)
          return false
        return item.fields.some(field => this.effectiveSummary[field])
      })
    },
    vehicleColorMap() {
      const map = {}
      this.vehicleLegend.forEach((vehicle, idx) => {
        map[String(vehicle.id)] = VEHICLE_COLORS[idx % VEHICLE_COLORS.length]
      })
      return map
    },
    showTimeCompare() {
      const summary = this.effectiveSummary
      return !!(
        summary.planned_start || summary.planned_finish
        || summary.hos_start || summary.hos_finish
        || summary.vehicle_start || summary.vehicle_finish
        || summary.ignition_start || summary.ignition_finish
        || (this.showDvirColumn && (summary.dvir_start || summary.dvir_finish))
      )
    },
    showDvirColumn() {
      return !!this.summary.dvirs_enabled
    },
    plannedStartText() {
      return this.summary.planned_start
        ? this.formatPlannedTime(this.summary.planned_start)
        : '—'
    },
    plannedFinishText() {
      return this.summary.planned_finish
        ? this.formatPlannedTime(this.summary.planned_finish)
        : '—'
    },
    actualStartText() {
      return this.effectiveSummary.hos_start
        ? this.formatActualTime(this.effectiveSummary.hos_start)
        : '—'
    },
    actualFinishText() {
      return this.effectiveSummary.hos_finish
        ? this.formatActualTime(this.effectiveSummary.hos_finish)
        : '—'
    },
    vehicleStartText() {
      return this.effectiveSummary.vehicle_start
        ? this.formatActualTime(this.effectiveSummary.vehicle_start)
        : '—'
    },
    vehicleFinishText() {
      return this.effectiveSummary.vehicle_finish
        ? this.formatActualTime(this.effectiveSummary.vehicle_finish)
        : '—'
    },
    ignitionStartText() {
      return this.effectiveSummary.ignition_start
        ? this.formatActualTime(this.effectiveSummary.ignition_start)
        : '—'
    },
    ignitionFinishText() {
      return this.effectiveSummary.ignition_finish
        ? this.formatActualTime(this.effectiveSummary.ignition_finish)
        : '—'
    },
    dvirStartText() {
      return this.effectiveSummary.dvir_start
        ? this.formatActualTime(this.effectiveSummary.dvir_start)
        : '—'
    },
    dvirFinishText() {
      return this.effectiveSummary.dvir_finish
        ? this.formatActualTime(this.effectiveSummary.dvir_finish)
        : '—'
    },
    plannedDurationText() {
      if (!this.summary.planned_start || !this.summary.planned_finish)
        return '—'
      return this.formatDuration(
        this.parseTimeMs(this.summary.planned_start),
        this.parseTimeMs(this.summary.planned_finish),
      )
    },
    actualDurationText() {
      const startMs = this.compareStartActualMs()
      const finishMs = this.compareFinishActualMs()
      if (startMs == null || finishMs == null)
        return '—'
      return this.formatDuration(startMs, finishMs)
    },
    vehicleDurationText() {
      if (!this.effectiveSummary.vehicle_start || !this.effectiveSummary.vehicle_finish)
        return '—'
      return this.formatDuration(
        this.parseTimeMs(this.effectiveSummary.vehicle_start),
        this.parseTimeMs(this.effectiveSummary.vehicle_finish),
      )
    },
    ignitionDurationText() {
      if (!this.effectiveSummary.ignition_start || !this.effectiveSummary.ignition_finish)
        return '—'
      return this.formatDuration(
        this.parseTimeMs(this.effectiveSummary.ignition_start),
        this.parseTimeMs(this.effectiveSummary.ignition_finish),
      )
    },
    dvirDurationText() {
      if (!this.effectiveSummary.dvir_start || !this.effectiveSummary.dvir_finish)
        return '—'
      return this.formatDuration(
        this.parseTimeMs(this.effectiveSummary.dvir_start),
        this.parseTimeMs(this.effectiveSummary.dvir_finish),
      )
    },
    durationDeltaMinutes() {
      if (!this.summary.planned_start || !this.summary.planned_finish)
        return null
      const compareStartMs = this.compareStartActualMs()
      const compareFinishMs = this.compareFinishActualMs()
      if (compareStartMs == null || compareFinishMs == null)
        return null
      const planned = Math.round(
        (this.parseTimeMs(this.summary.planned_finish) - this.parseTimeMs(this.summary.planned_start)) / 60000,
      )
      const actual = Math.round((compareFinishMs - compareStartMs) / 60000)
      return actual - planned
    },
    startDeltaMinutes() {
      if (!this.summary.planned_start)
        return null
      const compareStartMs = this.compareStartActualMs()
      if (compareStartMs == null)
        return null
      return Math.round(
        (compareStartMs - this.parseTimeMs(this.summary.planned_start)) / 60000,
      )
    },
    finishDeltaMinutes() {
      if (!this.summary.planned_finish)
        return null
      const compareFinishMs = this.compareFinishActualMs()
      if (compareFinishMs == null)
        return null
      return Math.round(
        (compareFinishMs - this.parseTimeMs(this.summary.planned_finish)) / 60000,
      )
    },
  },

  methods: {
    async copyBookingId() {
      const value = this.bookingIdToCopy
      if (!value)
        return
      try {
        await copyToClipboard(String(value))
        this.bookingIdCopied = true
        if (this.bookingIdCopiedTimer)
          clearTimeout(this.bookingIdCopiedTimer)
        this.bookingIdCopiedTimer = setTimeout(() => {
          this.bookingIdCopied = false
        }, 2000)
      } catch (e) {
        this.$emit('showAlert', 'unable to copy booking ID', 'danger', 3000)
      }
    },
    onModalFeedbackSaved(event) {
      this.$emit('feedback-saved', event)
    },
    snapshotCalculatedTimes() {
      const snapshot = {}
      EDITABLE_SUMMARY_TIME_FIELDS.forEach(field => {
        if (this.summary && this.summary[field])
          snapshot[field] = this.summary[field]
      })
      this.calculatedTimes = snapshot
    },
    toggleManualActualsMode() {
      if (this.manualActualsMode) {
        if (this.hasManualActualsEdits && !confirm('Discard unsaved manual actual time edits?'))
          return
        this.exitManualActualsMode()
        return
      }
      this.snapshotCalculatedTimes()
      this.editedTimes = {}
      this.manualActualsMode = true
    },
    exitManualActualsMode() {
      this.removeMarkerDragListeners()
      this.markerDrag = null
      this.manualActualsMode = false
      this.editedTimes = {}
    },
    resetManualActualsEdits() {
      this.editedTimes = {}
    },
    msToSummaryTime(ms) {
      const tz = this.summary && this.summary.timezone
      const m = tz ? moment.tz(ms, tz) : moment(ms)
      return m.milliseconds(0).format()
    },
    onMarkerMouseDown(event, marker, view, segIdx) {
      if (!this.manualActualsMode || !marker.field || event.button !== 0)
        return
      event.stopPropagation()
      const layer = event.currentTarget.parentElement
      if (!layer)
        return
      this.markerDrag = {
        field: marker.field,
        segIdx,
        viewWindowStartMs: view.windowStartMs,
        viewWindowEndMs: view.windowEndMs,
        rect: layer.getBoundingClientRect(),
      }
      this._markerDragMoveHandler = (moveEvent) => this.onMarkerMouseMove(moveEvent)
      this._markerDragUpHandler = () => this.onMarkerMouseUp()
      document.addEventListener('mousemove', this._markerDragMoveHandler)
      document.addEventListener('mouseup', this._markerDragUpHandler)
    },
    onMarkerMouseMove(event) {
      if (!this.markerDrag)
        return
      const pct = this.clientXToTimelinePct(event.clientX, this.markerDrag.rect)
      const span = this.markerDrag.viewWindowEndMs - this.markerDrag.viewWindowStartMs
      const ms = Math.round(this.markerDrag.viewWindowStartMs + (pct / 100) * span)
      const clampedMs = Math.min(
        this.markerDrag.viewWindowEndMs,
        Math.max(this.markerDrag.viewWindowStartMs, ms),
      )
      this.editedTimes = {
        ...this.editedTimes,
        [this.markerDrag.field]: this.msToSummaryTime(clampedMs),
      }
    },
    onMarkerMouseUp() {
      this.removeMarkerDragListeners()
      this.markerDrag = null
    },
    removeMarkerDragListeners() {
      if (this._markerDragMoveHandler) {
        document.removeEventListener('mousemove', this._markerDragMoveHandler)
        this._markerDragMoveHandler = null
      }
      if (this._markerDragUpHandler) {
        document.removeEventListener('mouseup', this._markerDragUpHandler)
        this._markerDragUpHandler = null
      }
    },
    manualTimeChanged(field) {
      if (!(field in this.editedTimes))
        return false
      const editedMs = this.parseTimeMs(this.editedTimes[field])
      const calculatedMs = this.parseTimeMs(this.calculatedTimes[field])
      if (editedMs == null && calculatedMs == null)
        return false
      if (editedMs == null || calculatedMs == null)
        return true
      return editedMs !== calculatedMs
    },
    buildManualActualTimesPayload() {
      const times = {}
      EDITABLE_SUMMARY_TIME_FIELDS.forEach(field => {
        if (!this.manualTimeChanged(field))
          return
        times[field] = this.editedTimes[field] || null
      })
      return times
    },
    async saveManualActualTimes() {
      const times = this.buildManualActualTimesPayload()
      if (!Object.keys(times).length) {
        this.$emit('showAlert', 'no changes to save', 'warning', 3000)
        return
      }
      if (!this.tenant) {
        this.$emit('showAlert', 'tenant is required to save manual actual times', 'danger', 4000)
        return
      }
      this.savingManualActuals = true
      try {
        let value
        if (this.isTbn) {
          const tbnBookingHash = (this.feedbackItem && this.feedbackItem.ID) || ''
          if (!tbnBookingHash) {
            this.$emit('showAlert', 'booking hash not available for save', 'danger', 4000)
            return
          }
          ({ value = {} } = await save_tbn_manual_actual_times(this.tenant, {
            tbn_booking_hash: tbnBookingHash,
            times,
          }))
        } else {
          const bookingId = (this.summary && this.summary.booking_id)
            || (this.feedbackItem && this.feedbackItem.bookingID)
            || ''
          if (!bookingId) {
            this.$emit('showAlert', 'booking ID not available for save', 'danger', 4000)
            return
          }
          ({ value = {} } = await save_manual_actual_times(this.tenant, {
            booking_id: bookingId,
            movement_id: (this.feedbackItem && this.feedbackItem.movementID)
              || (this.summary && this.summary.movement_id),
            driver_name: (this.summary && this.summary.driver_name)
              || (this.feedbackItem && this.feedbackItem.driver),
            times,
          }))
        }
        const payload = (value.data && (value.data.data || value.data)) || value
        if (payload.error || payload.success !== true) {
          this.$emit('showAlert', payload.error || 'save failed', 'danger', 6000)
          return
        }
        this.$emit('showAlert', 'manual actual times saved', 'success', 4000)
        this.exitManualActualsMode()
        this.$emit('manual-times-saved')
        this.$emit('reload')
      } catch (e) {
        const resp = e.response && e.response.data
        const payload = resp && (resp.data || resp)
        const msg = (payload && payload.error) ||
          (resp && resp.message) ||
          'error saving manual actual times, try again or contact support if needed'
        this.$emit('showAlert', msg, 'danger', 6000)
      } finally {
        this.savingManualActuals = false
      }
    },
    compareStartActualMs() {
      const summary = this.effectiveSummary
      if (summary.compare_start_source === 'vehicle')
        return this.parseTimeMs(summary.vehicle_start)
      return this.parseTimeMs(summary.hos_start)
    },
    compareFinishActualMs() {
      const summary = this.effectiveSummary
      if (summary.compare_finish_source === 'vehicle')
        return this.parseTimeMs(summary.vehicle_finish)
      return this.parseTimeMs(summary.hos_finish)
    },
    footerSegmentLabel(segmentLabel) {
      if (segmentLabel === 'start')
        return 'Start'
      if (segmentLabel === 'finish')
        return 'Finish'
      return 'Trip'
    },
    resolveTripDayValue(field) {
      const sources = [this.summary, this.feedbackItem]
      for (const source of sources) {
        if (!source)
          continue
        const value = source[field]
        if (value == null || value === '')
          continue
        const parsed = parseInt(value, 10)
        if (!Number.isNaN(parsed))
          return parsed
      }
      return null
    },
    tenantMoment(value) {
      const tz = this.summary && this.summary.timezone
      if (value == null || value === '')
        return moment.invalid()
      if (typeof value === 'number')
        return tz ? moment.tz(value, tz) : moment(value)
      if (tz)
        return moment.tz(value, tz)
      return moment(value)
    },
    parseTimeMs(value) {
      const m = this.tenantMoment(value)
      return m.isValid() ? m.valueOf() : null
    },
    getTimelineSegments() {
      if (this.summary.window_segments && this.summary.window_segments.length)
        return this.summary.window_segments
      return [{
        label: 'trip',
        window_start: this.summary.window_start,
        window_end: this.summary.window_end,
      }]
    },
    getSegmentBaseWindow(segIdx) {
      const segment = this.getTimelineSegments()[segIdx] || {}
      return {
        startMs: this.parseTimeMs(segment.window_start),
        endMs: this.parseTimeMs(segment.window_end),
      }
    },
    getEffectiveWindow(segIdx) {
      const base = this.getSegmentBaseWindow(segIdx)
      if (base.startMs == null || base.endMs == null)
        return base
      const zoom = this.timelineZoomBySegment[segIdx]
      if (!zoom)
        return base
      return {
        startMs: Math.max(base.startMs, zoom.startMs),
        endMs: Math.min(base.endMs, zoom.endMs),
      }
    },
    isSegmentZoomed(segIdx) {
      const base = this.getSegmentBaseWindow(segIdx)
      const effective = this.getEffectiveWindow(segIdx)
      if (base.startMs == null || base.endMs == null || effective.startMs == null || effective.endMs == null)
        return false
      return effective.startMs > base.startMs + TIMELINE_ZOOM_FULL_RANGE_TOLERANCE_MS
        || effective.endMs < base.endMs - TIMELINE_ZOOM_FULL_RANGE_TOLERANCE_MS
    },
    resetSegmentZoom(segIdx) {
      this.$delete(this.timelineZoomBySegment, segIdx)
    },
    resetAllTimelineZoom() {
      this.timelineZoomBySegment = {}
      this.zoomSelect = null
      this.removeZoomSelectListeners()
    },
    setSegmentZoom(segIdx, startMs, endMs) {
      const base = this.getSegmentBaseWindow(segIdx)
      if (base.startMs == null || base.endMs == null)
        return
      let start = Math.max(base.startMs, Math.min(startMs, endMs))
      let end = Math.min(base.endMs, Math.max(startMs, endMs))
      if (end <= start)
        return
      if (end - start < MIN_TIMELINE_ZOOM_SPAN_MS) {
        const center = (start + end) / 2
        start = center - MIN_TIMELINE_ZOOM_SPAN_MS / 2
        end = center + MIN_TIMELINE_ZOOM_SPAN_MS / 2
        if (start < base.startMs) {
          start = base.startMs
          end = Math.min(base.endMs, start + MIN_TIMELINE_ZOOM_SPAN_MS)
        }
        if (end > base.endMs) {
          end = base.endMs
          start = Math.max(base.startMs, end - MIN_TIMELINE_ZOOM_SPAN_MS)
        }
      }
      if (end - start >= (base.endMs - base.startMs) - TIMELINE_ZOOM_FULL_RANGE_TOLERANCE_MS) {
        this.resetSegmentZoom(segIdx)
        return
      }
      this.$set(this.timelineZoomBySegment, segIdx, {
        startMs: Math.round(start),
        endMs: Math.round(end),
      })
    },
    zoomTimelineIn(segIdx) {
      const current = this.getEffectiveWindow(segIdx)
      if (current.startMs == null || current.endMs == null)
        return
      const span = current.endMs - current.startMs
      const newSpan = Math.max(MIN_TIMELINE_ZOOM_SPAN_MS, span * 0.5)
      const center = (current.startMs + current.endMs) / 2
      this.setSegmentZoom(segIdx, center - newSpan / 2, center + newSpan / 2)
    },
    zoomTimelineOut(segIdx) {
      const base = this.getSegmentBaseWindow(segIdx)
      const current = this.getEffectiveWindow(segIdx)
      if (base.startMs == null || base.endMs == null || current.startMs == null || current.endMs == null)
        return
      const span = current.endMs - current.startMs
      const baseSpan = base.endMs - base.startMs
      const newSpan = Math.min(baseSpan, span * 2)
      if (newSpan >= baseSpan - TIMELINE_ZOOM_FULL_RANGE_TOLERANCE_MS) {
        this.resetSegmentZoom(segIdx)
        return
      }
      const center = (current.startMs + current.endMs) / 2
      let start = center - newSpan / 2
      let end = center + newSpan / 2
      if (start < base.startMs) {
        end += base.startMs - start
        start = base.startMs
      }
      if (end > base.endMs) {
        start -= end - base.endMs
        end = base.endMs
      }
      this.setSegmentZoom(segIdx, start, end)
    },
    panTimelineEarlier(segIdx) {
      const base = this.getSegmentBaseWindow(segIdx)
      const current = this.getEffectiveWindow(segIdx)
      if (base.startMs == null || current.startMs == null || current.endMs == null)
        return
      const span = current.endMs - current.startMs
      const shift = span * 0.25
      let start = current.startMs - shift
      let end = current.endMs - shift
      if (start < base.startMs) {
        start = base.startMs
        end = start + span
      }
      this.setSegmentZoom(segIdx, start, end)
    },
    panTimelineLater(segIdx) {
      const base = this.getSegmentBaseWindow(segIdx)
      const current = this.getEffectiveWindow(segIdx)
      if (base.endMs == null || current.startMs == null || current.endMs == null)
        return
      const span = current.endMs - current.startMs
      const shift = span * 0.25
      let start = current.startMs + shift
      let end = current.endMs + shift
      if (end > base.endMs) {
        end = base.endMs
        start = end - span
      }
      this.setSegmentZoom(segIdx, start, end)
    },
    canPanTimelineEarlier(segIdx) {
      const base = this.getSegmentBaseWindow(segIdx)
      const current = this.getEffectiveWindow(segIdx)
      return current.startMs != null && base.startMs != null
        && current.startMs > base.startMs + TIMELINE_ZOOM_FULL_RANGE_TOLERANCE_MS
    },
    canPanTimelineLater(segIdx) {
      const base = this.getSegmentBaseWindow(segIdx)
      const current = this.getEffectiveWindow(segIdx)
      return current.endMs != null && base.endMs != null
        && current.endMs < base.endMs - TIMELINE_ZOOM_FULL_RANGE_TOLERANCE_MS
    },
    canZoomTimelineIn(segIdx) {
      const current = this.getEffectiveWindow(segIdx)
      return current.startMs != null && current.endMs != null
        && (current.endMs - current.startMs) > MIN_TIMELINE_ZOOM_SPAN_MS + TIMELINE_ZOOM_FULL_RANGE_TOLERANCE_MS
    },
    canZoomTimelineOut(segIdx) {
      return this.isSegmentZoomed(segIdx)
    },
    relevantActivityWindowForSegment(segIdx) {
      const base = this.getSegmentBaseWindow(segIdx)
      if (base.startMs == null || base.endMs == null)
        return null
      const segment = this.getTimelineSegments()[segIdx] || {}
      return computeRelevantActivityWindow({
        windowStartMs: base.startMs,
        windowEndMs: base.endMs,
        segmentLabel: segment.label || 'trip',
        summary: this.summary,
        parseTimeMs: value => this.parseTimeMs(value),
      })
    },
    canZoomToRelevantActivityForSegment(segIdx) {
      if (this.isRelevantActivityZoomActive(segIdx))
        return true
      const base = this.getSegmentBaseWindow(segIdx)
      return canZoomToRelevantActivity({
        windowStartMs: base.startMs,
        windowEndMs: base.endMs,
        activityWindow: this.relevantActivityWindowForSegment(segIdx),
      })
    },
    isRelevantActivityZoomActive(segIdx) {
      if (!this.isSegmentZoomed(segIdx))
        return false
      const effective = this.getEffectiveWindow(segIdx)
      return isZoomedToRelevantActivity({
        activityWindow: this.relevantActivityWindowForSegment(segIdx),
        effectiveStartMs: effective.startMs,
        effectiveEndMs: effective.endMs,
      })
    },
    zoomToRelevantActivity(segIdx) {
      if (this.isRelevantActivityZoomActive(segIdx)) {
        this.resetSegmentZoom(segIdx)
        return
      }
      const activityWindow = this.relevantActivityWindowForSegment(segIdx)
      if (!activityWindow)
        return
      this.setSegmentZoom(segIdx, activityWindow.startMs, activityWindow.endMs)
    },
    clientXToTimelinePct(clientX, rect) {
      if (!rect || !rect.width)
        return 0
      return Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100))
    },
    onTimelineZoomMouseDown(event, segIdx, view) {
      if (this.manualActualsMode)
        return
      if (event.button !== 0)
        return
      const layer = event.currentTarget
      const rect = layer.getBoundingClientRect()
      const startPct = this.clientXToTimelinePct(event.clientX, rect)
      this.zoomSelect = { segIdx, startPct, currentPct: startPct, rect }
      this._zoomMoveHandler = (moveEvent) => this.onTimelineZoomMouseMove(moveEvent)
      this._zoomUpHandler = (upEvent) => this.onTimelineZoomMouseUp(upEvent, segIdx, view)
      document.addEventListener('mousemove', this._zoomMoveHandler)
      document.addEventListener('mouseup', this._zoomUpHandler)
    },
    onTimelineZoomMouseMove(event) {
      if (!this.zoomSelect)
        return
      this.zoomSelect.currentPct = this.clientXToTimelinePct(event.clientX, this.zoomSelect.rect)
    },
    onTimelineZoomMouseUp(event, segIdx, view) {
      this.removeZoomSelectListeners()
      if (!this.zoomSelect || this.zoomSelect.segIdx !== segIdx) {
        this.zoomSelect = null
        return
      }
      const startPct = Math.min(this.zoomSelect.startPct, this.zoomSelect.currentPct)
      const endPct = Math.max(this.zoomSelect.startPct, this.zoomSelect.currentPct)
      this.zoomSelect = null
      if (endPct - startPct < 1)
        return
      const startMs = view.windowStartMs + (startPct / 100) * view.windowSpanMs
      const endMs = view.windowStartMs + (endPct / 100) * view.windowSpanMs
      this.setSegmentZoom(segIdx, startMs, endMs)
    },
    removeZoomSelectListeners() {
      if (this._zoomMoveHandler) {
        document.removeEventListener('mousemove', this._zoomMoveHandler)
        this._zoomMoveHandler = null
      }
      if (this._zoomUpHandler) {
        document.removeEventListener('mouseup', this._zoomUpHandler)
        this._zoomUpHandler = null
      }
    },
    zoomSelectionStyle(segIdx) {
      if (!this.zoomSelect || this.zoomSelect.segIdx !== segIdx)
        return {}
      const left = Math.min(this.zoomSelect.startPct, this.zoomSelect.currentPct)
      const width = Math.abs(this.zoomSelect.currentPct - this.zoomSelect.startPct)
      return {
        left: left + '%',
        width: width + '%',
      }
    },
    buildTimelineView(segment) {
      const windowStartMs = this.parseTimeMs(segment.window_start)
      const windowEndMs = this.parseTimeMs(segment.window_end)
      if (windowStartMs == null || windowEndMs == null || windowEndMs <= windowStartMs)
        return null
      const windowSpanMs = windowEndMs - windowStartMs
      const view = {
        windowStartMs,
        windowEndMs,
        windowSpanMs,
        segmentLabel: segment.label || 'trip',
      }

      const hourTicks = []
      let tick = this.tenantMoment(windowStartMs).startOf('hour')
      if (tick.valueOf() < windowStartMs)
        tick.add(1, 'hour')
      let index = 0
      while (tick.valueOf() <= windowEndMs) {
        const ms = tick.valueOf()
        hourTicks.push({
          ms,
          leftPct: ((ms - windowStartMs) / windowSpanMs) * 100,
          label: index % 2 === 0 ? this.formatAxisTick(tick) : '',
        })
        tick.add(1, 'hour')
        index += 1
      }

      const dateChangeTicks = [{
        ms: windowStartMs,
        leftPct: 0,
        label: this.tenantMoment(windowStartMs).format('MM/DD'),
      }]
      let midnight = this.tenantMoment(windowStartMs).startOf('day').add(1, 'day')
      while (midnight.valueOf() <= windowEndMs) {
        if (midnight.valueOf() > windowStartMs) {
          dateChangeTicks.push({
            ms: midnight.valueOf(),
            leftPct: ((midnight.valueOf() - windowStartMs) / windowSpanMs) * 100,
            label: midnight.format('MM/DD'),
          })
        }
        midnight.add(1, 'day')
      }
      const endLabel = this.tenantMoment(windowEndMs).format('MM/DD')
      const lastTick = dateChangeTicks[dateChangeTicks.length - 1]
      if (!lastTick || lastTick.leftPct < 96 || lastTick.label !== endLabel) {
        dateChangeTicks.push({
          ms: windowEndMs,
          leftPct: 100,
          label: endLabel,
        })
      }

      const midnightGridLines = []
      let midnightCursor = this.tenantMoment(windowStartMs).startOf('day').add(1, 'day')
      while (midnightCursor.valueOf() < windowEndMs) {
        if (midnightCursor.valueOf() > windowStartMs) {
          midnightGridLines.push({
            ms: midnightCursor.valueOf(),
            leftPct: ((midnightCursor.valueOf() - windowStartMs) / windowSpanMs) * 100,
          })
        }
        midnightCursor.add(1, 'day')
      }

      const hosStateSegments = this.buildHosStateSegments(view)
      const hosLogHits = this.buildHosLogHits(view)
      const vehicleSegments = this.buildVehicleSegments(view)
      const ignitionStateSegments = this.buildIgnitionStateSegments(view)
      const ignitionLogHits = this.buildIgnitionLogHits(view)
      const markers = this.buildTimelineMarkers(view)
      const dvirMarkers = this.buildDvirMarkers(view)

      return {
        windowStartMs: view.windowStartMs,
        windowEndMs: view.windowEndMs,
        windowSpanMs: view.windowSpanMs,
        segmentLabel: view.segmentLabel,
        hourTicks,
        dateChangeTicks,
        midnightGridLines,
        hosLogHits,
        hosStepPath: this.buildHosStepPath(hosStateSegments, view),
        vehicleSegments,
        showVehicleRow: vehicleSegments.length > 0,
        ignitionLogHits,
        ignitionStepPath: this.buildIgnitionStepPath(ignitionStateSegments, view),
        showIgnitionRow: true,
        dvirMarkers,
        markers,
      }
    },
    buildHosStateSegments(view) {
      const intervals = (this.events || [])
        .map(event => ({
          status: event.status,
          label: event.label || STATUS_LABELS[event.status] || event.status,
          start: Math.max(view.windowStartMs, this.parseTimeMs(event.start)),
          end: Math.min(view.windowEndMs, this.parseTimeMs(event.end)),
          remark: event.remark || '',
          vehicle_id: event.vehicle_id,
          vehicle_name: event.vehicle_name || '',
          is_booking_vehicle: !!event.is_booking_vehicle,
        }))
        .filter(event => event.end > event.start)
        .sort((a, b) => a.start - b.start || a.end - b.end)

      if (!intervals.length)
        return []

      const merged = []
      for (const interval of intervals) {
        const last = merged[merged.length - 1]
        if (last && last.status === interval.status && interval.start <= last.end) {
          last.end = Math.max(last.end, interval.end)
          if (!last.remark && interval.remark)
            last.remark = interval.remark
        } else {
          merged.push({ ...interval })
        }
      }
      return merged
    },
    buildHosLogHits(view) {
      return (this.events || [])
        .map(event => {
          const start = Math.max(view.windowStartMs, this.parseTimeMs(event.start))
          const end = Math.min(view.windowEndMs, this.parseTimeMs(event.end))
          return {
            status: event.status,
            start,
            end,
            vehicle_id: event.vehicle_id,
            vehicle_name: event.vehicle_name || '',
            is_booking_vehicle: !!event.is_booking_vehicle,
          }
        })
        .filter(log => log.end > log.start)
    },
    buildVehicleSegments(view) {
      const intervals = (this.events || [])
        .filter(event => event.vehicle_id && String(event.vehicle_id) !== '0')
        .map(event => ({
          start: Math.max(view.windowStartMs, this.parseTimeMs(event.start)),
          end: Math.min(view.windowEndMs, this.parseTimeMs(event.end)),
          vehicle_id: String(event.vehicle_id),
          vehicle_name: event.vehicle_name || '',
          is_booking_vehicle: !!event.is_booking_vehicle,
        }))
        .filter(event => event.end > event.start)
        .sort((a, b) => a.start - b.start || a.end - b.end)

      if (!intervals.length)
        return []

      const merged = []
      for (const interval of intervals) {
        const last = merged[merged.length - 1]
        if (last && last.vehicle_id === interval.vehicle_id && interval.start <= last.end) {
          last.end = Math.max(last.end, interval.end)
          if (!last.vehicle_name && interval.vehicle_name)
            last.vehicle_name = interval.vehicle_name
          last.is_booking_vehicle = last.is_booking_vehicle || interval.is_booking_vehicle
        } else {
          merged.push({ ...interval })
        }
      }

      return merged.map(segment => ({
        ...segment,
        label: this.vehicleSegmentLabel(segment),
      }))
    },
    ignitionEventsForView(view) {
      const segmentLabel = view.segmentLabel || 'trip'
      return (this.ignitionEvents || []).filter(event => (event.segment || 'trip') === segmentLabel)
    },
    buildIgnitionStateSegments(view) {
      const intervals = this.ignitionEventsForView(view)
        .map(event => ({
          status: event.status,
          label: event.label || IGNITION_STATUS_LABELS[event.status] || event.status,
          start: Math.max(view.windowStartMs, this.parseTimeMs(event.start)),
          end: Math.min(view.windowEndMs, this.parseTimeMs(event.end)),
        }))
        .filter(event => event.end > event.start)
        .sort((a, b) => a.start - b.start || a.end - b.end)

      if (!intervals.length)
        return []

      const merged = []
      for (const interval of intervals) {
        const last = merged[merged.length - 1]
        if (last && last.status === interval.status && interval.start <= last.end) {
          last.end = Math.max(last.end, interval.end)
        } else {
          merged.push({ ...interval })
        }
      }
      return merged
    },
    buildIgnitionLogHits(view) {
      return this.ignitionEventsForView(view)
        .map(event => {
          const start = Math.max(view.windowStartMs, this.parseTimeMs(event.start))
          const end = Math.min(view.windowEndMs, this.parseTimeMs(event.end))
          return {
            status: event.status,
            label: event.label || IGNITION_STATUS_LABELS[event.status] || event.status,
            start,
            end,
          }
        })
        .filter(segment => segment.end > segment.start)
    },
    vehicleSegmentLabel(segment) {
      const resolvedName = segment.vehicle_name
      if (resolvedName && String(resolvedName) !== String(segment.vehicle_id))
        return resolvedName
      const bookingVehicleId = this.summary && this.summary.booking_vehicle_id
      if (this.summary.vehicle && bookingVehicleId
        && String(bookingVehicleId) === String(segment.vehicle_id))
        return this.summary.vehicle
      return resolvedName || segment.vehicle_id
    },
    buildTimelineMarkers(view) {
      const markers = []
      const summary = this.effectiveSummary
      const label = view.segmentLabel || 'trip'
      const addMarker = (time, className, title, field = null) => {
        const ms = this.parseTimeMs(time)
        if (ms == null || ms < view.windowStartMs || ms > view.windowEndMs)
          return
        markers.push({ ms, className, title, field })
      }
      const addStartMarkers = () => {
        addMarker(
          summary.planned_start,
          'hos-marker-planned',
          'Planned start: ' + this.formatGranularTime(summary.planned_start),
        )
        addMarker(
          summary.hos_start,
          'hos-marker-matched',
          'HOS start: ' + this.formatGranularTime(summary.hos_start),
          'hos_start',
        )
        addMarker(
          summary.vehicle_start,
          'hos-marker-vehicle',
          'Vehicle start leave: ' + this.formatGranularTime(summary.vehicle_start),
          'vehicle_start',
        )
        addMarker(
          summary.ignition_start,
          'hos-marker-ignition',
          'Ignition on: ' + this.formatGranularTime(summary.ignition_start),
          'ignition_start',
        )
      }
      const addFinishMarkers = () => {
        addMarker(
          summary.planned_finish,
          'hos-marker-planned',
          'Planned finish: ' + this.formatGranularTime(summary.planned_finish),
        )
        addMarker(
          summary.hos_finish,
          'hos-marker-matched',
          'HOS finish: ' + this.formatGranularTime(summary.hos_finish),
          'hos_finish',
        )
        addMarker(
          summary.vehicle_finish,
          'hos-marker-vehicle',
          'Vehicle finish arrival: ' + this.formatGranularTime(summary.vehicle_finish),
          'vehicle_finish',
        )
        addMarker(
          summary.ignition_finish,
          'hos-marker-ignition',
          'Ignition off: ' + this.formatGranularTime(summary.ignition_finish),
          'ignition_finish',
        )
      }
      if (label === 'start') {
        addStartMarkers()
      } else if (label === 'finish') {
        addFinishMarkers()
      } else {
        addStartMarkers()
        addFinishMarkers()
      }
      return markers
    },
    buildDvirMarkers(view) {
      const markers = []
      const summary = this.effectiveSummary
      const label = view.segmentLabel || 'trip'
      const addMarker = (time, title, field) => {
        const ms = this.parseTimeMs(time)
        if (ms == null || ms < view.windowStartMs || ms > view.windowEndMs)
          return
        markers.push({ ms, className: 'hos-marker-dvir', title, field })
      }
      if (label === 'start' || label === 'trip') {
        if (summary.dvir_start) {
          addMarker(
            summary.dvir_start,
            'DVIR pre trip: ' + this.formatGranularTime(summary.dvir_start),
            'dvir_start',
          )
        }
      }
      if (label === 'finish' || label === 'trip') {
        if (summary.dvir_finish) {
          addMarker(
            summary.dvir_finish,
            'DVIR post trip: ' + this.formatGranularTime(summary.dvir_finish),
            'dvir_finish',
          )
        }
      }
      return markers
    },
    buildHosStepPath(segments, view) {
      if (!segments.length)
        return ''

      let path = ''
      segments.forEach((segment, idx) => {
        const xStart = this.timeToViewX(segment.start, view)
        const xEnd = this.timeToViewX(segment.end, view)
        const y = this.statusToViewY(segment.status)
        if (idx === 0)
          path += 'M ' + xStart + ' ' + y + ' '
        path += 'L ' + xEnd + ' ' + y + ' '
        if (idx < segments.length - 1) {
          const nextY = this.statusToViewY(segments[idx + 1].status)
          path += 'L ' + xEnd + ' ' + nextY + ' '
        }
      })
      return path
    },
    buildIgnitionStepPath(segments, view) {
      if (!segments.length)
        return ''

      let path = ''
      segments.forEach((segment, idx) => {
        const xStart = this.timeToViewX(segment.start, view)
        const xEnd = this.timeToViewX(segment.end, view)
        const y = this.ignitionStatusToViewY(segment.status)
        if (idx === 0)
          path += 'M ' + xStart + ' ' + y + ' '
        path += 'L ' + xEnd + ' ' + y + ' '
        if (idx < segments.length - 1) {
          const nextY = this.ignitionStatusToViewY(segments[idx + 1].status)
          path += 'L ' + xEnd + ' ' + nextY + ' '
        }
      })
      return path
    },
    statusLabel(status) {
      return STATUS_LABELS[status] || status
    },
    timeToViewX(ms, view) {
      return ((ms - view.windowStartMs) / view.windowSpanMs) * 100
    },
    statusToViewY(status) {
      const level = STATUS_LEVELS[status] != null ? STATUS_LEVELS[status] : 0
      const padding = 8
      const span = STATE_LINE_VIEW_HEIGHT - (padding * 2)
      return padding + (level * (span / 2))
    },
    ignitionStatusToViewY(status) {
      const level = IGNITION_STATUS_LEVELS[status] != null ? IGNITION_STATUS_LEVELS[status] : 0
      const padding = 8
      const span = STATE_LINE_VIEW_HEIGHT - (padding * 2)
      return padding + (level * (span / 2))
    },
    stateHitStyle(segment, view) {
      const startMs = segment.start
      const endMs = segment.end
      const leftPct = ((startMs - view.windowStartMs) / view.windowSpanMs) * 100
      const widthPct = Math.max(0.4, ((endMs - startMs) / view.windowSpanMs) * 100)
      return {
        left: leftPct + '%',
        width: widthPct + '%',
      }
    },
    vehicleColor(vehicleId) {
      return this.vehicleColorMap[String(vehicleId)] || '#666'
    },
    referenceMarkerKey(className) {
      const item = REFERENCE_MARKER_LEGEND.find(entry => entry.className === className)
      return item ? item.key : null
    },
    isReferenceMarkerVisible(className) {
      const key = this.referenceMarkerKey(className)
      return key == null || this.referenceMarkerVisibility[key] !== false
    },
    visibleTimelineMarkers(view) {
      return (view.markers || []).filter(marker => this.isReferenceMarkerVisible(marker.className))
    },
    visibleDvirMarkers(view) {
      if (!this.referenceMarkerVisibility.dvir)
        return []
      return view.dvirMarkers || []
    },
    toggleReferenceMarker(key) {
      this.referenceMarkerVisibility = {
        ...this.referenceMarkerVisibility,
        [key]: !this.referenceMarkerVisibility[key],
      }
    },
    resetReferenceMarkerVisibility() {
      this.referenceMarkerVisibility = { ...DEFAULT_REFERENCE_MARKER_VISIBILITY }
    },
    ignitionHitStyle(segment, view) {
      const startMs = segment.start
      const endMs = segment.end
      const leftPct = ((startMs - view.windowStartMs) / view.windowSpanMs) * 100
      const widthPct = Math.max(0.4, ((endMs - startMs) / view.windowSpanMs) * 100)
      return {
        left: leftPct + '%',
        width: widthPct + '%',
      }
    },
    markerStyle(marker, view) {
      const pct = ((marker.ms - view.windowStartMs) / view.windowSpanMs) * 100
      return { left: Math.min(100, Math.max(0, pct)) + '%' }
    },
    formatGranularTime(value) {
      const m = this.tenantMoment(value)
      if (!m.isValid())
        return value
      if (m.milliseconds() || m.seconds())
        return m.format('MM/DD hh:mm:ss A')
      return m.format('MM/DD hh:mm A')
    },
    formatPlannedTime(value) {
      const m = this.tenantMoment(value)
      return m.isValid() ? m.format('M/D h:mm A') : value
    },
    formatActualTime(value) {
      const m = this.tenantMoment(value)
      if (!m.isValid())
        return value
      if (m.seconds() || m.milliseconds())
        return m.format('M/D h:mm:ss A')
      return m.format('M/D h:mm A')
    },
    formatDuration(startMs, endMs) {
      const minutes = Math.max(1, Math.round((endMs - startMs) / 60000))
      if (minutes < 60)
        return minutes + 'm'
      const hours = Math.floor(minutes / 60)
      const rem = minutes % 60
      return rem ? (hours + 'h ' + rem + 'm') : (hours + 'h')
    },
    formatSignedDelta(minutes) {
      if (minutes == null)
        return '—'
      const sign = minutes < 0 ? '−' : '+'
      const abs = Math.abs(minutes)
      if (abs < 60)
        return sign + abs + 'm'
      const hours = Math.floor(abs / 60)
      const rem = abs % 60
      return rem ? (sign + hours + 'h ' + rem + 'm') : (sign + hours + 'h')
    },
    deltaClass(minutes) {
      if (minutes == null || minutes === 0)
        return 'hos-time-delta-neutral'
      if (minutes < 0)
        return 'hos-time-delta-negative'
      return 'hos-time-delta-positive'
    },
    logHoverTitle(log) {
      const vehicle = log.vehicle_name || log.vehicle_id || '(none)'
      return [
        'Status: ' + this.statusLabel(log.status),
        'Start: ' + this.formatGranularTime(log.start),
        'Finish: ' + this.formatGranularTime(log.end),
        'Duration: ' + this.formatDuration(log.start, log.end),
        'Vehicle: ' + vehicle,
      ].join('\n')
    },
    ignitionHoverTitle(segment) {
      return [
        'Ignition: ' + (segment.label || IGNITION_STATUS_LABELS[segment.status] || segment.status),
        'Start: ' + this.formatGranularTime(segment.start),
        'Finish: ' + this.formatGranularTime(segment.end),
        'Duration: ' + this.formatDuration(segment.start, segment.end),
      ].join('\n')
    },
    formatAxisTick(tickMoment) {
      return tickMoment.format('h A')
    },
    axisTickAlignClass(leftPct) {
      if (leftPct <= 4)
        return 'hos-timeline-axis-tick--start'
      if (leftPct >= 96)
        return 'hos-timeline-axis-tick--end'
      return ''
    },
    vehicleSegmentStyle(segment, view) {
      const leftPct = ((segment.start - view.windowStartMs) / view.windowSpanMs) * 100
      const widthPct = Math.max(1.2, ((segment.end - segment.start) / view.windowSpanMs) * 100)
      const style = {
        left: leftPct + '%',
        width: widthPct + '%',
      }
      if (this.hasMultipleVehicles)
        style.borderLeftColor = this.vehicleColor(segment.vehicle_id)
      return style
    },
    vehicleSegmentTitle(segment) {
      const vehicle = this.vehicleSegmentLabel(segment)
      return [
        'Vehicle: ' + vehicle,
        'Start: ' + this.formatGranularTime(segment.start),
        'Finish: ' + this.formatGranularTime(segment.end),
        'Duration: ' + this.formatDuration(segment.start, segment.end),
      ].join('\n')
    },
  },
}
</script>

<style scoped>
.hos-time-compare {
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #eee;
}
.hos-timeline-error-banner {
  margin-bottom: 8px;
  color: #dc3545;
  font-size: 13px;
  font-weight: 600;
}
.hos-time-compare-grid {
  display: grid;
  grid-template-columns: auto minmax(88px, 1fr) minmax(88px, 1fr) minmax(88px, 1fr) minmax(88px, 1fr) minmax(56px, auto);
  grid-template-rows: auto auto auto auto;
  gap: 1px;
  align-items: stretch;
  font-size: 12px;
  line-height: 1.3;
  color: #444;
  background: #e9ecef;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  overflow: hidden;
}
.hos-time-compare-grid-with-dvir {
  grid-template-columns: auto minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(80px, 1fr) minmax(56px, auto);
}
.hos-time-compare-spacer,
.hos-time-compare-head,
.hos-time-compare-label,
.hos-time-compare-value {
  background: #fff;
  padding: 5px 8px;
}
.hos-time-compare-head {
  font-weight: 600;
  color: #666;
  font-size: 11px;
  white-space: nowrap;
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
}
.hos-time-compare-label {
  white-space: nowrap;
  color: #666;
  font-weight: 500;
  display: flex;
  align-items: center;
}
.hos-time-compare-value {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: #333;
  display: flex;
  align-items: center;
}
.hos-time-planned {
  color: #0d6efd;
}
.hos-time-matched {
  color: #198754;
}
.hos-time-vehicle {
  color: #6f42c1;
}
.hos-time-ignition {
  color: #d63384;
}
.hos-time-dvir {
  color: #fd7e14;
}
.hos-time-compare-delta {
  font-weight: 600;
  font-size: 13px;
}
.hos-time-delta-negative {
  color: #dc3545;
}
.hos-time-delta-positive {
  color: #198754;
}
.hos-time-delta-neutral {
  color: #666;
}
.hos-timeline-legend-heading {
  font-weight: 600;
  margin-right: 2px;
  color: #666;
}
.hos-timeline-legend-booking {
  color: #888;
}
.hos-timeline-vehicle-legend {
  margin-top: -2px;
}
.hos-timeline-vehicle-swatch {
  display: inline-block;
  width: 0;
  height: 10px;
  border-left: 3px solid #666;
}
.hos-timeline-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 11px;
  color: #555;
}
.hos-timeline-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.hos-timeline-reference-legend {
  margin-top: -2px;
}
.hos-timeline-legend-toggle {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 6px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  font-size: 11px;
  color: #555;
  cursor: pointer;
  line-height: 1.2;
}
.hos-timeline-legend-toggle:hover {
  background: #f8f9fa;
  border-color: #e9ecef;
}
.hos-timeline-legend-toggle--off {
  opacity: 0.5;
}
.hos-timeline-legend-toggle--off .hos-timeline-reference-swatch {
  border-left-style: dotted;
}
.hos-timeline-reference-swatch {
  display: inline-block;
  width: 0;
  height: 12px;
  border-left: 2px dashed #666;
}
.hos-timeline-reference-edit-hint {
  color: #888;
  font-style: italic;
}
.hos-timeline-marker--draggable {
  pointer-events: auto;
  cursor: ew-resize;
  width: 10px;
  margin-left: -5px;
  transform: none;
}
.hos-timeline-marker--dragging {
  opacity: 0.85;
  z-index: 10;
}
.hos-timeline-chart-wrapper-split {
  margin-top: 4px;
}
.hos-timeline-chart-wrapper-split + .hos-timeline-chart-wrapper-split {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #dee2e6;
}
.hos-timeline-zoom-controls {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 16.5rem;
  flex-shrink: 0;
}
.hos-timeline-chart-wrapper {
  margin-top: 4px;
}
.hos-timeline-axis-row {
  display: flex;
  align-items: stretch;
  margin-bottom: 4px;
}
.hos-timeline-axis-row-bottom {
  margin-top: 4px;
  margin-bottom: 0;
}
.hos-timeline-axis-spacer {
  width: 120px;
  min-width: 120px;
  flex-shrink: 0;
}
.hos-timeline-axis-track {
  position: relative;
  flex: 1;
  min-height: 22px;
  font-size: 11px;
  color: #666;
}
.hos-timeline-axis-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  white-space: nowrap;
  line-height: 1.2;
}
.hos-timeline-axis-tick--start {
  transform: translateX(0);
}
.hos-timeline-axis-tick--end {
  transform: translateX(-100%);
}
.hos-timeline-date-row {
  margin-bottom: 2px;
}
.hos-timeline-date-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  white-space: nowrap;
  line-height: 1.2;
  font-size: 11px;
  font-weight: 600;
  color: #444;
}
.hos-timeline-date-tick.hos-timeline-axis-tick--start {
  transform: translateX(0);
}
.hos-timeline-date-tick.hos-timeline-axis-tick--end {
  transform: translateX(-100%);
}
.hos-timeline-grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px solid rgba(0, 0, 0, 0.1);
  pointer-events: none;
  z-index: 0;
}
.hos-timeline-chart-midnight-grid {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 120px;
  right: 0;
  pointer-events: none;
  z-index: 1;
}
.hos-timeline-midnight-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 1px solid rgba(0, 0, 0, 0.22);
  pointer-events: none;
}
.hos-timeline-chart {
  position: relative;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}
.hos-timeline-chart-reference-markers {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 120px;
  right: 0;
  pointer-events: none;
  z-index: 9;
}
.hos-timeline-chart-reference-markers .hos-timeline-marker {
  pointer-events: auto;
}
.hos-timeline-zoom-layer {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 120px;
  right: 0;
  z-index: 7;
  pointer-events: none;
  user-select: none;
}
.hos-timeline-zoom-selection {
  position: absolute;
  top: 0;
  bottom: 0;
  background: rgba(13, 110, 253, 0.12);
  border: 1px solid rgba(13, 110, 253, 0.45);
  pointer-events: none;
}
.hos-timeline-row {
  display: flex;
  align-items: stretch;
  border-top: 1px solid #eee;
}
.hos-timeline-row:first-child {
  border-top: none;
}
.hos-timeline-row-label {
  width: 120px;
  min-width: 120px;
  padding: 8px 6px;
  font-size: 11px;
  font-weight: 600;
  background: #fafbfc;
  border-right: 1px solid #eee;
  color: #666;
}
.hos-timeline-track {
  position: relative;
  flex: 1;
  height: 42px;
  background: #fff;
  cursor: crosshair;
}
.hos-timeline-state-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 8px;
  padding-bottom: 8px;
  font-size: 10px;
  line-height: 1.1;
}
.hos-timeline-state-track {
  height: 72px;
}
.hos-state-band {
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
  z-index: 0;
}
.hos-state-band-offDuty {
  top: 0;
  height: 33.33%;
  background: rgba(101, 114, 129, 0.08);
}
.hos-state-band-driving {
  top: 33.33%;
  height: 33.34%;
  background: rgba(13, 176, 16, 0.08);
}
.hos-state-band-onDuty {
  top: 66.67%;
  height: 33.33%;
  background: rgba(251, 149, 86, 0.08);
}
.hos-state-line {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  overflow: visible;
}
.hos-state-line-path {
  fill: none;
  stroke: #000;
  stroke-width: 2px;
  vector-effect: non-scaling-stroke;
  stroke-linecap: square;
  stroke-linejoin: miter;
}
.hos-state-hit {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  cursor: default;
}
.hos-state-hit:hover {
  background: rgba(0, 0, 0, 0.06);
}
.hos-timeline-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 0;
  transform: translateX(-1px);
  pointer-events: none;
}
.hos-marker-planned {
  border-left: 2px dashed #0d6efd;
  z-index: 2;
}
.hos-marker-matched {
  border-left: 2px dashed #198754;
  z-index: 3;
}
.hos-marker-vehicle {
  border-left: 2px dashed #6f42c1;
  z-index: 2;
}
.hos-marker-ignition {
  border-left: 2px dashed #d63384;
  z-index: 2;
}
.hos-marker-dvir {
  border-left: 2px dashed #fd7e14;
  z-index: 2;
}
.hos-timeline-vehicle-track {
  height: 34px;
}
.hos-timeline-vehicle-segment {
  position: absolute;
  top: 4px;
  height: 26px;
  border: 1px solid #ced4da;
  border-left: 4px solid #6c757d;
  border-radius: 3px;
  background: #f8f9fa;
  box-sizing: border-box;
  overflow: hidden;
  z-index: 1;
}
.hos-timeline-vehicle-segment-booking {
  border-color: #9ec5fe;
  background: #eef5ff;
}
.hos-timeline-vehicle-label {
  position: absolute;
  left: 6px;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 10px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hos-timeline-ignition-labels {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 6px;
  padding-bottom: 6px;
  font-size: 10px;
  line-height: 1.1;
}
.hos-timeline-ignition-track {
  height: 72px;
}
.hos-ignition-band {
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
  z-index: 0;
}
.hos-ignition-band-off {
  top: 0;
  height: 33.33%;
  background: rgba(101, 114, 129, 0.08);
}
.hos-ignition-band-on {
  top: 33.33%;
  height: 33.34%;
  background: rgba(25, 135, 84, 0.1);
}
.hos-ignition-band-idle {
  top: 66.67%;
  height: 33.33%;
  background: rgba(255, 193, 7, 0.12);
}
.hos-ignition-line {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
  overflow: visible;
}
.hos-ignition-line-path {
  fill: none;
  stroke: #d63384;
  stroke-width: 2px;
  vector-effect: non-scaling-stroke;
  stroke-linecap: square;
  stroke-linejoin: miter;
}
.hos-ignition-hit {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 2;
  cursor: default;
}
.hos-ignition-hit:hover {
  background: rgba(214, 51, 132, 0.08);
}
.hos-timeline-empty {
  margin-top: 12px;
  font-style: italic;
  color: #666;
}
</style>

<style>
.hos-timeline-modal-footer {
  border-top: none;
}
.hos-timeline-modal-footer-wrap {
  width: 100%;
}
.hos-timeline-modal-footer-inner {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  gap: 12px;
}
.hos-timeline-modal-footer-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  justify-self: start;
}
.hos-timeline-modal-footer-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  justify-self: center;
}
.hos-timeline-footer-segment-switch {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 4px;
}
.hos-timeline-footer-segment-btn {
  border: 1px solid #ced4da;
  background: #f8f9fa;
  color: #495057;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.hos-timeline-footer-segment-btn-active {
  border-color: #0d6efd;
  background: #e7f1ff;
  color: #0d6efd;
}
.hos-timeline-modal-footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: end;
}
.hos-timeline-modal-nav {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.hos-timeline-nav-label {
  font-size: 13px;
  color: #495057;
  min-width: 3.5rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.hos-timeline-processed-time-footer {
  flex: 1;
  text-align: center;
  font-size: 13px;
  color: #666;
}
.hos-timeline-modal-footer-secondary {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #dee2e6;
  gap: 12px;
}
.hos-timeline-modal-footer-manual,
.hos-timeline-modal-footer-admin {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.hos-timeline-modal-footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.hos-timeline-icon-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  font-size: 20px;
  padding: 6px 10px;
  line-height: 1;
  border-radius: 4px;
  transition: color 0.15s, background-color 0.15s;
}
.hos-timeline-icon-btn-active {
  color: #0d6efd;
  background-color: #e7f1ff;
}
.hos-timeline-icon-btn:hover:not(:disabled) {
  color: #0d6efd;
  background-color: #f1f3f5;
}
.hos-timeline-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.hos-timeline-modal-feedback.actuals-feedback-controls {
  margin-top: 0;
}
.hos-timeline-modal-feedback .actuals-feedback-btn {
  font-size: 16px;
  padding: 4px 8px;
}
</style>
